import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
    Lightning, VideoCamera, Gauge, Thermometer,
    BatteryCharging, MapPin, Timer, ArrowLeft,
    Eye, EyeSlash, Camera
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

export default function ARMode() {
    const { user } = useAuth();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [cameraActive, setCameraActive] = useState(false);
    const [hudVisible, setHudVisible] = useState(true);
    const [telemetry, setTelemetry] = useState({
        speed: 0, voltage: 72.0, current: 0, rpm: 0,
        motorTemp: 45, ctrlTemp: 35, battery: 85, distance: 0,
    });
    const [rideActive, setRideActive] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [alerts, setAlerts] = useState([]);
    const intervalRef = useRef(null);
    const timerRef = useRef(null);

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
                audio: false,
            });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
                setCameraActive(true);
                toast.success('AR Camera active');
            }
        } catch {
            toast.error('Camera access denied. AR mode requires camera permission.');
        }
    };

    const stopCamera = () => {
        if (videoRef.current?.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(t => t.stop());
            videoRef.current.srcObject = null;
        }
        setCameraActive(false);
    };

    const simulateTelemetry = useCallback(() => {
        setTelemetry(prev => {
            const speed = rideActive ? Math.min(prev.speed + (Math.random() - 0.3) * 8, 120) : Math.max(prev.speed - 2, 0);
            const current = speed > 0 ? 10 + speed * 1.5 + Math.random() * 20 : 0;
            const motorTemp = Math.min(prev.motorTemp + (current > 100 ? 0.3 : -0.1), 150);
            const battery = Math.max(prev.battery - (current > 0 ? 0.01 : 0), 0);

            const newAlerts = [];
            if (motorTemp > 110) newAlerts.push({ type: 'critical', msg: 'MOTOR OVERHEAT' });
            if (battery < 15) newAlerts.push({ type: 'warning', msg: 'LOW BATTERY' });
            if (speed > 100) newAlerts.push({ type: 'info', msg: 'SPEED WARNING' });
            setAlerts(newAlerts);

            return {
                speed: Math.max(0, Math.round(speed)),
                voltage: 72 - (100 - battery) * 0.2,
                current: Math.round(current),
                rpm: Math.round(speed * 50),
                motorTemp: Math.round(motorTemp),
                ctrlTemp: Math.round(35 + current * 0.15),
                battery: Math.round(battery * 10) / 10,
                distance: prev.distance + (speed > 0 ? speed / 3600 : 0),
            };
        });
    }, [rideActive]);

    useEffect(() => {
        intervalRef.current = setInterval(simulateTelemetry, 500);
        return () => clearInterval(intervalRef.current);
    }, [simulateTelemetry]);

    useEffect(() => {
        if (rideActive) {
            timerRef.current = setInterval(() => setElapsed(p => p + 1), 1000);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [rideActive]);

    const toggleRide = () => {
        if (rideActive) {
            setRideActive(false);
            toast.success(`Ride ended — ${telemetry.distance.toFixed(2)} km`);
        } else {
            setRideActive(true);
            setElapsed(0);
            setTelemetry(p => ({ ...p, distance: 0 }));
            toast.success('Ride started!');
        }
    };

    const formatTime = (s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        return () => { stopCamera(); };
    }, []);

    const speedPercent = Math.min(telemetry.speed / 120, 1);
    const batteryColor = telemetry.battery > 50 ? '#00FF8A' : telemetry.battery > 20 ? '#FFB400' : '#FF2020';

    return (
        <div className="fixed inset-0 bg-black z-50 overflow-hidden" data-testid="ar-mode-container">
            {/* Camera feed */}
            <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted data-testid="ar-camera-feed" />

            {/* Dark overlay if no camera */}
            {!cameraActive && (
                <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/70 to-black/90 flex flex-col items-center justify-center">
                    <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                        <VideoCamera size={64} className="text-primary mx-auto mb-4" />
                        <h2 className="text-2xl font-display text-primary text-center mb-2">BOLTORIUM AR MODE</h2>
                        <p className="text-sm text-muted-foreground text-center mb-6 max-w-xs">
                            Real-time heads-up display overlaid on your camera. See speed, battery, temps, and alerts while riding.
                        </p>
                        <Button onClick={startCamera} className="bg-primary text-black font-display text-lg px-8 py-3" data-testid="start-camera-btn">
                            <Camera size={20} className="mr-2" /> Launch AR Camera
                        </Button>
                    </motion.div>
                </div>
            )}

            {/* HUD Overlay */}
            <AnimatePresence>
                {hudVisible && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="absolute inset-0 pointer-events-none" data-testid="ar-hud-overlay">

                        {/* Top bar */}
                        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between pointer-events-auto">
                            <a href="/dashboard" className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/10" data-testid="ar-back-btn">
                                <ArrowLeft size={20} className="text-white" />
                            </a>
                            <div className="flex gap-2">
                                <Badge className="bg-black/50 backdrop-blur-sm border-primary/30 text-primary text-xs font-mono">
                                    <MapPin size={12} className="mr-1" /> AR MODE
                                </Badge>
                                {rideActive && (
                                    <Badge className="bg-primary/20 backdrop-blur-sm border-primary/30 text-primary text-xs font-mono animate-pulse">
                                        <Timer size={12} className="mr-1" /> {formatTime(elapsed)}
                                    </Badge>
                                )}
                            </div>
                            <button onClick={() => setHudVisible(false)} className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/10" data-testid="ar-hide-hud-btn">
                                <EyeSlash size={20} className="text-white" />
                            </button>
                        </div>

                        {/* Center speedometer — hidden on splash */}
                        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${!cameraActive ? 'opacity-0 pointer-events-none' : ''}`}>
                            <div className="relative w-48 h-48" data-testid="ar-speedometer">
                                {/* Speed ring */}
                                <svg viewBox="0 0 200 200" className="w-full h-full">
                                    <circle cx="100" cy="100" r="90" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                                    <circle cx="100" cy="100" r="90" fill="none" stroke={speedPercent > 0.8 ? '#FF2020' : '#00FF8A'}
                                        strokeWidth="6" strokeLinecap="round"
                                        strokeDasharray={`${speedPercent * 565} 565`}
                                        transform="rotate(-90 100 100)"
                                        style={{ filter: `drop-shadow(0 0 8px ${speedPercent > 0.8 ? '#FF2020' : '#00FF8A'})` }} />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-5xl font-black font-mono text-white" style={{ textShadow: '0 0 20px rgba(0,255,138,0.5)' }}>
                                        {telemetry.speed}
                                    </span>
                                    <span className="text-xs text-white/60 font-display tracking-widest">KM/H</span>
                                </div>
                            </div>
                        </div>

                        {/* Left panel — power */}
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 space-y-3">
                            <HudGauge label="VOLTS" value={telemetry.voltage.toFixed(1)} unit="V" color="#FFB400" icon={Lightning} />
                            <HudGauge label="AMPS" value={telemetry.current} unit="A" color="#00C8FF" icon={Lightning} />
                            <HudGauge label="RPM" value={telemetry.rpm} unit="" color="#7A00FF" icon={Gauge} />
                        </div>

                        {/* Right panel — health */}
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 space-y-3">
                            <HudGauge label="MOTOR" value={telemetry.motorTemp} unit="°C" color={telemetry.motorTemp > 100 ? '#FF2020' : '#00FF8A'} icon={Thermometer} />
                            <HudGauge label="CTRL" value={telemetry.ctrlTemp} unit="°C" color={telemetry.ctrlTemp > 70 ? '#FF2020' : '#00C8FF'} icon={Thermometer} />
                            <HudGauge label="BATT" value={telemetry.battery.toFixed(0)} unit="%" color={batteryColor} icon={BatteryCharging} />
                        </div>

                        {/* Bottom bar */}
                        <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-auto">
                            <div className="flex items-center justify-between gap-4 max-w-lg mx-auto">
                                <div className="text-center flex-1">
                                    <p className="text-[10px] text-white/40 font-display">DISTANCE</p>
                                    <p className="text-lg font-mono text-white font-bold">{telemetry.distance.toFixed(2)} km</p>
                                </div>

                                <Button onClick={toggleRide}
                                    className={`rounded-full w-16 h-16 text-lg font-display ${rideActive ? 'bg-plasma-red text-white hover:bg-plasma-red/80' : 'bg-primary text-black hover:bg-primary/80'}`}
                                    data-testid="ar-ride-toggle">
                                    {rideActive ? 'STOP' : 'GO'}
                                </Button>

                                <div className="text-center flex-1">
                                    <p className="text-[10px] text-white/40 font-display">ELAPSED</p>
                                    <p className="text-lg font-mono text-white font-bold">{formatTime(elapsed)}</p>
                                </div>
                            </div>
                        </div>

                        {/* Alerts */}
                        <div className="absolute top-20 left-1/2 -translate-x-1/2 space-y-2">
                            <AnimatePresence>
                                {alerts.map((a, i) => (
                                    <motion.div key={a.msg} initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}
                                        className={`px-4 py-2 rounded-full backdrop-blur-md text-sm font-display font-bold text-center ${
                                            a.type === 'critical' ? 'bg-plasma-red/30 border border-plasma-red/50 text-plasma-red' :
                                            a.type === 'warning' ? 'bg-boltz-gold/30 border border-boltz-gold/50 text-boltz-gold' :
                                            'bg-primary/30 border border-primary/50 text-primary'
                                        }`} data-testid={`ar-alert-${i}`}>
                                        {a.msg}
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* HUD toggle (when hidden) */}
            {!hudVisible && (
                <button onClick={() => setHudVisible(true)}
                    className="absolute bottom-6 right-6 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/20 z-50"
                    data-testid="ar-show-hud-btn">
                    <Eye size={24} className="text-white" />
                </button>
            )}
        </div>
    );
}

/* ─── Mini HUD Gauge Component ─── */
function HudGauge({ label, value, unit, color, icon: Icon }) {
    return (
        <div className="bg-black/40 backdrop-blur-md rounded-xl border border-white/10 px-3 py-2 min-w-[80px]"
            style={{ borderColor: `${color}33` }}>
            <div className="flex items-center gap-1 mb-0.5">
                <Icon size={10} style={{ color }} />
                <span className="text-[8px] font-display tracking-wider" style={{ color: `${color}99` }}>{label}</span>
            </div>
            <p className="text-lg font-mono font-black text-white leading-none">
                {value}<span className="text-[10px] ml-0.5" style={{ color: `${color}99` }}>{unit}</span>
            </p>
        </div>
    );
}
